import { buildLocalProxyHttpRequest } from './build-local-proxy-http-request.js';
import { buildLocalHttpRequestOptions, loadHttpModule } from './run-local-http-request.js';
import { LOCAL_PROXY_REQUEST_TIMEOUT_MS } from './constants.js';
import { incomingMessageHeaders } from './incoming-message-headers.js';
import type { ProxyRequest, ProxyStreamCallbacks } from './types.js';

/** Proxy a request to a local server and stream the response via callbacks. */
export async function proxyToLocalStreaming(
  request: ProxyRequest,
  callbacks: ProxyStreamCallbacks,
): Promise<void> {
  const built = buildLocalProxyHttpRequest(request);
  if ('error' in built) {
    callbacks.onError(built.error);
    return;
  }

  const isHttps = request.url.startsWith('https:');
  const mod = await loadHttpModule(isHttps);
  const opts = buildLocalHttpRequestOptions(built);

  await new Promise<void>((resolve) => {
    const req = mod.request(opts, (res) => {
      try {
        callbacks.onStart(res.statusCode ?? 0, incomingMessageHeaders(res));
      } catch (err) {
        req.destroy();
        callbacks.onError(err instanceof Error ? err.message : String(err));
        resolve();
        return;
      }

      res.on('data', (chunk: Buffer) => {
        try {
          callbacks.onChunk(new Uint8Array(chunk));
        } catch (err) {
          req.destroy();
          callbacks.onError(err instanceof Error ? err.message : String(err));
        }
      });

      res.on('end', () => {
        callbacks.onEnd();
        resolve();
      });

      res.on('error', (err) => {
        callbacks.onError(err.message);
        resolve();
      });
    });

    req.setTimeout(LOCAL_PROXY_REQUEST_TIMEOUT_MS, () => {
      req.destroy(new Error(`Local preview request timed out after ${LOCAL_PROXY_REQUEST_TIMEOUT_MS}ms`));
    });

    req.on('error', (err) => {
      callbacks.onError(err.message);
      resolve();
    });

    if (built.sendingBody && built.body != null) {
      req.write(built.body);
    }
    req.end();
  });
}
